import { useState, useEffect, useCallback, useRef } from 'react';
import DashboardLayout from '@/components/layout/DashboardLayout';
import { tenantsApi, mediaApi } from '@/services/api';
import { useSettings } from '@/context/SettingsContext';

type SettingsTab = 'general' | 'features' | 'social' | 'seo';

interface GeneralSettings {
  site_name: string;
  tagline: string;
  contact_email: string;
  contact_phone: string;
  primary_phone: string;
  primary_phone_raw: string;
  whatsapp_number: string;
  address: string;
  currency: string;
  date_format: string;
  logo_url: string;
  favicon_url: string;
  show_site_name: boolean;
}

interface FeatureToggles {
  property_ratings: boolean;
  enquiries_count: boolean;
  posted_date: boolean;
  user_registration: boolean;
  require_email: boolean;
  karnataka_cities_page: boolean;
}

interface SocialLinks {
  facebook: string;
  instagram: string;
  twitter: string;
  linkedin: string;
  youtube: string;
}

interface SeoDefaults {
  default_title: string;
  default_description: string;
  default_keywords: string;
  og_image_url: string;
}

interface SeoPageOverride {
  page: string;
  title: string;
  description: string;
  keywords: string;
}

const defaultGeneral: GeneralSettings = {
  site_name: 'CashMyProperty.ai',
  tagline: 'AI-Powered Land Monetization',
  contact_email: 'support@cashmyproperty.ai',
  contact_phone: '+91-80-4567-8900',
  primary_phone: '+91 90350 63455',
  primary_phone_raw: '+919035063455',
  whatsapp_number: '+919035063455',
  address: '42, MG Road, Bangalore - 560001, India',
  currency: 'INR',
  date_format: 'DD/MM/YYYY',
  logo_url: '',
  favicon_url: '',
  show_site_name: true,
};

const defaultFeatures: FeatureToggles = {
  property_ratings: true,
  enquiries_count: true,
  posted_date: true,
  user_registration: true,
  require_email: false,
  karnataka_cities_page: true,
};

const defaultSocial: SocialLinks = {
  facebook: 'https://facebook.com/cashmyproperty',
  instagram: 'https://instagram.com/cashmyproperty',
  twitter: 'https://x.com/cashmyproperty',
  linkedin: 'https://linkedin.com/company/cashmyproperty',
  youtube: 'https://youtube.com/@cashmyproperty',
};

const defaultSeo: SeoDefaults = {
  default_title: 'CashMyProperty.ai — AI-Powered Land Monetization Platform',
  default_description: 'Unlock the hidden value of your land with India\'s first AI-powered land monetization platform. JDAs, long-term leases, franchise placements, and AI feasibility reports.',
  default_keywords: 'land monetization, AI land valuation, JDA development, lease placement, property monetization India',
  og_image_url: '',
};

const seoPages: string[] = [
  'home', 'about', 'services', 'listings', 'contact', 'faq', 'ai-technology',
  'how-it-works', 'pricing', 'case-studies', 'feasibility-report', 'partners',
];

const defaultOverrides: SeoPageOverride[] = seoPages.map(page => ({
  page,
  title: '',
  description: '',
  keywords: '',
}));

export default function SettingsPage() {
  const [activeTab, setActiveTab] = useState<SettingsTab>('general');
  const [actionMsg, setActionMsg] = useState<{ type: 'success' | 'error'; text: string } | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [loadError, setLoadError] = useState<string | null>(null);

  const clearMsg = useCallback(() => {
    const t = setTimeout(() => setActionMsg(null), 4000);
    return () => clearTimeout(t);
  }, []);

  // General settings state
  const [general, setGeneral] = useState<GeneralSettings>(defaultGeneral);
  const [features, setFeatures] = useState<FeatureToggles>(defaultFeatures);
  const [social, setSocial] = useState<SocialLinks>(defaultSocial);
  const [seoDefaults, setSeoDefaults] = useState<SeoDefaults>(defaultSeo);
  const [seoOverrides, setSeoOverrides] = useState<SeoPageOverride[]>(defaultOverrides);

  // Brand asset upload state
  const logoInputRef = useRef<HTMLInputElement>(null);
  const faviconInputRef = useRef<HTMLInputElement>(null);
  const [uploadingLogo, setUploadingLogo] = useState(false);
  const [uploadingFavicon, setUploadingFavicon] = useState(false);
  const { refresh } = useSettings();

  const handleAssetUpload = async (type: 'logo' | 'favicon', file: File) => {
    const field = type === 'logo' ? 'logo_url' : 'favicon_url';
    const label = type === 'logo' ? 'Logo' : 'Favicon';
    const setUploading = type === 'logo' ? setUploadingLogo : setUploadingFavicon;
    setUploading(true);
    try {
      const formData = new FormData();
      formData.append('images', file);
      const res = await mediaApi.upload(formData);
      const url = res.files?.[0];
      if (!url) {
        setActionMsg({ type: 'error', text: 'Upload returned no file URL. Please try again.' });
        clearMsg();
        return;
      }

      // Persist immediately so the public site reflects the change right away
      await tenantsApi.updateSettings({ general: { [field]: url } });
      setGeneral(prev => ({ ...prev, [field]: url }));
      await refresh();

      setActionMsg({ type: 'success', text: `${label} uploaded & saved.` });
      clearMsg();
    } catch (err: unknown) {
      const msg = err instanceof Error ? err.message : 'Upload failed. Try again.';
      setActionMsg({ type: 'error', text: msg });
      clearMsg();
    } finally {
      setUploading(false);
    }
  };

  const handleRemoveAsset = async (type: 'logo' | 'favicon') => {
    const field = type === 'logo' ? 'logo_url' : 'favicon_url';
    const label = type === 'logo' ? 'Logo' : 'Favicon';
    try {
      await tenantsApi.updateSettings({ general: { [field]: '' } });
      setGeneral(prev => ({ ...prev, [field]: '' }));
      await refresh();
      setActionMsg({ type: 'success', text: `${label} removed & saved.` });
      clearMsg();
    } catch (err: unknown) {
      const msg = err instanceof Error ? err.message : 'Failed to remove asset.';
      setActionMsg({ type: 'error', text: msg });
      clearMsg();
    }
  };

  // Load settings from backend on mount
  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    tenantsApi.getSettings()
      .then(res => {
        if (cancelled) return;
        const s = res.data as Record<string, unknown>;
        if (s?.general) setGeneral(prev => ({ ...prev, ...(s.general as Partial<GeneralSettings>) }));
        if (s?.features) setFeatures(prev => ({ ...prev, ...(s.features as Partial<FeatureToggles>) }));
        if (s?.social) setSocial(prev => ({ ...prev, ...(s.social as Partial<SocialLinks>) }));
        if (s?.seo) {
          const seo = s.seo as Record<string, unknown>;
          if (seo.defaults) setSeoDefaults(prev => ({ ...prev, ...(seo.defaults as Partial<SeoDefaults>) }));
          if (Array.isArray(seo.overrides)) {
            setSeoOverrides(prev => {
              const merged = [...prev];
              (seo.overrides as SeoPageOverride[]).forEach(ov => {
                const idx = merged.findIndex(m => m.page === ov.page);
                if (idx >= 0) merged[idx] = { ...merged[idx], ...ov };
                else merged.push(ov);
              });
              return merged;
            });
          }
        }
        setLoadError(null);
        setLoading(false);
      })
      .catch((err: unknown) => {
        if (!cancelled) {
          const msg = err instanceof Error ? err.message : 'Could not load settings';
          setLoadError(msg);
          setLoading(false);
        }
      });
    return () => { cancelled = true; };
  }, []);

  const handleSave = async (section: string) => {
    setSaving(true);
    try {
      const payload: Record<string, unknown> = {};
      if (section === 'general') payload.general = general;
      if (section === 'features') payload.features = features;
      if (section === 'social') payload.social = social;
      if (section === 'seo') payload.seo = { defaults: seoDefaults, overrides: seoOverrides };

      await tenantsApi.updateSettings(payload);
      setActionMsg({ type: 'success', text: `${section.charAt(0).toUpperCase() + section.slice(1)} settings saved.` });
    } catch (err: unknown) {
      const msg = err instanceof Error ? err.message : 'Failed to save. Check your connection and try again.';
      setActionMsg({ type: 'error', text: msg });
    } finally {
      setSaving(false);
      clearMsg();
    }
  };

  // Handle feature toggle
  const handleToggle = (key: keyof FeatureToggles) => {
    setFeatures(prev => ({ ...prev, [key]: !prev[key] }));
  };

  // Handle SEO override change
  const handleOverrideChange = (page: string, field: 'title' | 'description' | 'keywords', value: string) => {
    setSeoOverrides(prev => {
      const idx = prev.findIndex(o => o.page === page);
      if (idx >= 0) {
        const updated = [...prev];
        updated[idx] = { ...updated[idx], [field]: value };
        return updated;
      }
      return [...prev, { page, title: '', description: '', keywords: '', [field]: value }];
    });
  };

  // Get override value
  const getOverride = (page: string, field: 'title' | 'description' | 'keywords'): string => {
    const found = seoOverrides.find(o => o.page === page);
    return found ? found[field] : '';
  };

  const overrideFilledCount = seoOverrides.filter(o => o.title || o.description || o.keywords).length;

  return (
    <DashboardLayout>
      <div className="space-y-5 max-w-4xl">
        {/* Toast */}
        {actionMsg && (
          <div className={`fixed top-4 right-4 z-[1100] px-4 py-3 rounded-xl text-sm font-semibold shadow-lg ${
            actionMsg.type === 'success'
              ? 'bg-teal-500/20 text-teal-400 border border-teal-500/30'
              : 'bg-rose-500/20 text-rose-400 border border-rose-500/30'
          }`}>
            {actionMsg.text}
          </div>
        )}

        <div>
          <h1 className="font-heading text-xl font-bold text-foreground-950">Settings</h1>
          <p className="text-xs text-foreground-500">Platform configuration — changes save to your backend</p>
        </div>

        {/* Tab navigation */}
        <div className="flex items-center gap-1 bg-background-50 rounded-xl p-1 w-fit flex-wrap">
          {[
            { key: 'general' as SettingsTab, label: 'General', icon: 'ri-settings-line' },
            { key: 'features' as SettingsTab, label: 'Feature Toggles', icon: 'ri-toggle-line' },
            { key: 'social' as SettingsTab, label: 'Social & Branding', icon: 'ri-share-line' },
            { key: 'seo' as SettingsTab, label: 'SEO Meta Tags', icon: 'ri-search-eye-line' },
          ].map(tab => (
            <button
              key={tab.key}
              onClick={() => setActiveTab(tab.key)}
              className={`flex items-center gap-1.5 px-4 py-2 rounded-lg text-sm whitespace-nowrap cursor-pointer transition-all ${
                activeTab === tab.key
                  ? 'bg-accent-100 text-accent-700 font-semibold'
                  : 'text-foreground-500 hover:text-foreground-950'
              }`}
            >
              <i className={tab.icon} /> {tab.label}
            </button>
          ))}
        </div>

        {loading ? (
          <div className="rounded-xl border border-background-200/60 bg-white p-8 space-y-4">
            {Array.from({ length: 4 }).map((_, i) => (
              <div key={i} className="animate-pulse space-y-2">
                <div className="h-3 bg-background-800 rounded w-24" />
                <div className="h-10 bg-background-800 rounded" />
              </div>
            ))}
          </div>
        ) : loadError ? (
          <div className="rounded-xl border border-rose-500/20 bg-rose-50/50 p-8 text-center">
            <div className="w-12 h-12 mx-auto rounded-full bg-rose-500/15 flex items-center justify-center mb-3">
              <i className="ri-error-warning-line text-rose-400 text-xl" />
            </div>
            <h3 className="text-sm font-semibold text-foreground-950 mb-1">Could not load settings</h3>
            <p className="text-xs text-foreground-500 mb-4 max-w-md mx-auto">{loadError}</p>
            <button
              onClick={() => { setLoadError(null); setLoading(true); }}
              className="px-5 py-2.5 rounded-xl bg-accent-500 text-white text-sm font-semibold hover:bg-accent-400 cursor-pointer whitespace-nowrap"
            >
              <i className="ri-refresh-line mr-1.5" /> Retry
            </button>
          </div>
        ) : (
          <>
            {/* ── General Settings ── */}
            {activeTab === 'general' && (
              <div className="rounded-xl border border-background-200/60 bg-white p-6 space-y-5">
                <div>
                  <h3 className="text-sm font-semibold text-foreground-950 mb-4">General Information</h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="text-[10px] font-semibold text-foreground-500 uppercase tracking-wider block mb-1.5">Site Name</label>
                      <input type="text" value={general.site_name} onChange={e => setGeneral(prev => ({ ...prev, site_name: e.target.value }))} className="w-full px-4 py-2.5 rounded-xl bg-white border border-background-200/60 text-sm text-foreground-950 placeholder:text-foreground-500 focus:outline-none focus:border-accent-500/50" />
                    </div>
                    <div>
                      <label className="text-[10px] font-semibold text-foreground-500 uppercase tracking-wider block mb-1.5">Tagline</label>
                      <input type="text" value={general.tagline} onChange={e => setGeneral(prev => ({ ...prev, tagline: e.target.value }))} className="w-full px-4 py-2.5 rounded-xl bg-white border border-background-200/60 text-sm text-foreground-950 placeholder:text-foreground-500 focus:outline-none focus:border-accent-500/50" />
                    </div>
                    <div>
                      <label className="text-[10px] font-semibold text-foreground-500 uppercase tracking-wider block mb-1.5">Contact Email</label>
                      <input type="email" value={general.contact_email} onChange={e => setGeneral(prev => ({ ...prev, contact_email: e.target.value }))} className="w-full px-4 py-2.5 rounded-xl bg-white border border-background-200/60 text-sm text-foreground-950 placeholder:text-foreground-500 focus:outline-none focus:border-accent-500/50" />
                    </div>
                    <div>
                      <label className="text-[10px] font-semibold text-foreground-500 uppercase tracking-wider block mb-1.5">Contact Phone</label>
                      <input type="text" value={general.contact_phone} onChange={e => setGeneral(prev => ({ ...prev, contact_phone: e.target.value }))} className="w-full px-4 py-2.5 rounded-xl bg-white border border-background-200/60 text-sm text-foreground-950 placeholder:text-foreground-500 focus:outline-none focus:border-accent-500/50" />
                    </div>
                    <div>
                      <label className="text-[10px] font-semibold text-foreground-500 uppercase tracking-wider block mb-1.5">Display Phone <span className="text-foreground-400 font-normal">(shown on site, e.g. +91 90350 63455)</span></label>
                      <input type="text" value={general.primary_phone} onChange={e => setGeneral(prev => ({ ...prev, primary_phone: e.target.value }))} placeholder="+91 90350 63455" className="w-full px-4 py-2.5 rounded-xl bg-white border border-background-200/60 text-sm text-foreground-950 placeholder:text-foreground-500 focus:outline-none focus:border-accent-500/50" />
                    </div>
                    <div>
                      <label className="text-[10px] font-semibold text-foreground-500 uppercase tracking-wider block mb-1.5">Phone (tel: link) <span className="text-foreground-400 font-normal">(raw number for click-to-call, e.g. +919035063455)</span></label>
                      <input type="text" value={general.primary_phone_raw} onChange={e => setGeneral(prev => ({ ...prev, primary_phone_raw: e.target.value }))} placeholder="+919035063455" className="w-full px-4 py-2.5 rounded-xl bg-white border border-background-200/60 text-sm text-foreground-950 placeholder:text-foreground-500 focus:outline-none focus:border-accent-500/50" />
                    </div>
                    <div>
                      <label className="text-[10px] font-semibold text-foreground-500 uppercase tracking-wider block mb-1.5">WhatsApp Number</label>
                      <input type="text" value={general.whatsapp_number} onChange={e => setGeneral(prev => ({ ...prev, whatsapp_number: e.target.value }))} placeholder="+919035063455" className="w-full px-4 py-2.5 rounded-xl bg-white border border-background-200/60 text-sm text-foreground-950 placeholder:text-foreground-500 focus:outline-none focus:border-accent-500/50" />
                    </div>
                    <div className="md:col-span-2">
                      <label className="text-[10px] font-semibold text-foreground-500 uppercase tracking-wider block mb-1.5">Address</label>
                      <input type="text" value={general.address} onChange={e => setGeneral(prev => ({ ...prev, address: e.target.value }))} className="w-full px-4 py-2.5 rounded-xl bg-white border border-background-200/60 text-sm text-foreground-950 placeholder:text-foreground-500 focus:outline-none focus:border-accent-500/50" />
                    </div>
                  </div>
                </div>

                <div className="border-t border-background-200/60 pt-5">
                  <h3 className="text-sm font-semibold text-foreground-950 mb-4">Brand Assets</h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="text-[10px] font-semibold text-foreground-500 uppercase tracking-wider block mb-1.5">Logo</label>
                      <input
                        ref={logoInputRef}
                        type="file"
                        accept="image/png,image/svg+xml,image/jpeg,image/webp"
                        className="sr-only"
                        onChange={e => {
                          const file = e.target.files?.[0];
                          if (file) handleAssetUpload('logo', file);
                          e.target.value = '';
                        }}
                      />
                      <div
                        onClick={() => logoInputRef.current?.click()}
                        className="border-2 border-dashed border-background-200/60 rounded-xl p-4 text-center hover:border-accent-300 transition-colors cursor-pointer relative overflow-hidden min-h-[120px] flex flex-col items-center justify-center"
                      >
                        {general.logo_url ? (
                          <img
                            src={general.logo_url}
                            alt="Site logo"
                            className="max-h-16 max-w-full object-contain mx-auto"
                          />
                        ) : (
                          <>
                            <i className="ri-image-add-line text-2xl text-foreground-500 mb-1 block" />
                            <p className="text-xs text-foreground-500">Upload logo</p>
                            <p className="text-[10px] text-foreground-500">PNG, SVG (max 2MB)</p>
                          </>
                        )}
                        {uploadingLogo && (
                          <div className="absolute inset-0 bg-background-50/80 flex items-center justify-center">
                            <div className="w-6 h-6 rounded-full border-2 border-accent-500/30 border-t-accent-500 animate-spin" />
                          </div>
                        )}
                      </div>
                      {general.logo_url && (
                        <div className="mt-2 flex items-center gap-2">
                          <p className="text-[10px] text-foreground-400 truncate flex-1">{general.logo_url}</p>
                          <button
                            type="button"
                            onClick={() => handleRemoveAsset('logo')}
                            className="text-[10px] px-2 py-1 rounded-md border border-rose-500/30 text-rose-400 hover:bg-rose-500/10 cursor-pointer whitespace-nowrap"
                          >
                            Remove
                          </button>
                        </div>
                      )}
                    </div>
                    <div>
                      <label className="text-[10px] font-semibold text-foreground-500 uppercase tracking-wider block mb-1.5">Favicon</label>
                      <input
                        ref={faviconInputRef}
                        type="file"
                        accept="image/x-icon,image/png,image/jpeg"
                        className="sr-only"
                        onChange={e => {
                          const file = e.target.files?.[0];
                          if (file) handleAssetUpload('favicon', file);
                          e.target.value = '';
                        }}
                      />
                      <div
                        onClick={() => faviconInputRef.current?.click()}
                        className="border-2 border-dashed border-background-200/60 rounded-xl p-4 text-center hover:border-accent-300 transition-colors cursor-pointer relative overflow-hidden min-h-[120px] flex flex-col items-center justify-center"
                      >
                        {general.favicon_url ? (
                          <img
                            src={general.favicon_url}
                            alt="Site favicon"
                            className="max-h-12 max-w-full object-contain mx-auto"
                          />
                        ) : (
                          <>
                            <i className="ri-image-add-line text-2xl text-foreground-500 mb-1 block" />
                            <p className="text-xs text-foreground-500">Upload favicon</p>
                            <p className="text-[10px] text-foreground-500">ICO, PNG (64x64)</p>
                          </>
                        )}
                        {uploadingFavicon && (
                          <div className="absolute inset-0 bg-background-50/80 flex items-center justify-center">
                            <div className="w-6 h-6 rounded-full border-2 border-accent-500/30 border-t-accent-500 animate-spin" />
                          </div>
                        )}
                      </div>
                      {general.favicon_url && (
                        <div className="mt-2 flex items-center gap-2">
                          <p className="text-[10px] text-foreground-400 truncate flex-1">{general.favicon_url}</p>
                          <button
                            type="button"
                            onClick={() => handleRemoveAsset('favicon')}
                            className="text-[10px] px-2 py-1 rounded-md border border-rose-500/30 text-rose-400 hover:bg-rose-500/10 cursor-pointer whitespace-nowrap"
                          >
                            Remove
                          </button>
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Show site name toggle */}
                  <div className="mt-4 flex items-center justify-between py-3 border-t border-background-200/50">
                    <div>
                      <p className="text-sm text-foreground-950">Display Site Name Text</p>
                      <p className="text-[11px] text-foreground-500">Show the site name next to the logo in the public navbar</p>
                    </div>
                    <button
                      onClick={() => setGeneral(prev => ({ ...prev, show_site_name: !prev.show_site_name }))}
                      className={`relative w-11 h-6 rounded-full transition-colors cursor-pointer ${general.show_site_name ? 'bg-accent-500' : 'bg-background-200/50'}`}
                    >
                      <span className={`absolute top-0.5 w-5 h-5 rounded-full bg-white transition-transform ${general.show_site_name ? 'translate-x-[22px]' : 'translate-x-0.5'}`} />
                    </button>
                  </div>
                </div>

                <div className="border-t border-background-200/60 pt-5">
                  <h3 className="text-sm font-semibold text-foreground-950 mb-4">Localization</h3>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="text-[10px] font-semibold text-foreground-500 uppercase tracking-wider block mb-1.5">Currency Symbol</label>
                      <select value={general.currency} onChange={e => setGeneral(prev => ({ ...prev, currency: e.target.value }))} className="w-full px-4 py-2.5 rounded-xl bg-white border border-background-200/60 text-sm text-foreground-950 cursor-pointer">
                        <option value="INR">₹ (Indian Rupee)</option>
                        <option value="USD">$ (USD)</option>
                        <option value="EUR">€ (Euro)</option>
                        <option value="GBP">£ (GBP)</option>
                      </select>
                    </div>
                    <div>
                      <label className="text-[10px] font-semibold text-foreground-500 uppercase tracking-wider block mb-1.5">Date Format</label>
                      <select value={general.date_format} onChange={e => setGeneral(prev => ({ ...prev, date_format: e.target.value }))} className="w-full px-4 py-2.5 rounded-xl bg-white border border-background-200/60 text-sm text-foreground-950 cursor-pointer">
                        <option value="DD/MM/YYYY">DD/MM/YYYY</option>
                        <option value="MM/DD/YYYY">MM/DD/YYYY</option>
                        <option value="YYYY-MM-DD">YYYY-MM-DD</option>
                      </select>
                    </div>
                  </div>
                </div>

                <div className="flex justify-end pt-2">
                  <button onClick={() => handleSave('general')} disabled={saving} className="px-6 py-2.5 rounded-xl bg-accent-500 text-white text-sm font-semibold hover:bg-accent-400 disabled:opacity-60 cursor-pointer whitespace-nowrap">
                    {saving ? 'Saving...' : 'Save Changes'}
                  </button>
                </div>
              </div>
            )}

            {/* ── Feature Toggles ── */}
            {activeTab === 'features' && (
              <div className="rounded-xl border border-background-200/60 bg-white p-6 space-y-1">
                <h3 className="text-sm font-semibold text-foreground-950 mb-4">Feature Toggles</h3>
                {[
                  { key: 'property_ratings' as const, label: 'Property Ratings Display', desc: 'Show ratings on listing cards and detail pages' },
                  { key: 'enquiries_count' as const, label: 'Enquiries Count Display', desc: 'Show enquiry counts on listing cards' },
                  { key: 'posted_date' as const, label: 'Posted Date Display', desc: 'Show listing posted date on cards and detail pages' },
                  { key: 'user_registration' as const, label: 'User Registration', desc: 'Allow new users to register on the platform' },
                  { key: 'require_email' as const, label: 'Require Email Verification', desc: 'Users must verify email before accessing features' },
                  { key: 'karnataka_cities_page' as const, label: 'Karnataka Cities Page', desc: 'Show the Karnataka cities directory in navigation and allow visitors to browse it' },
                ].map(feature => (
                  <div key={feature.key} className="flex items-center justify-between py-3 border-b border-background-200/50 last:border-0">
                    <div>
                      <p className="text-sm text-foreground-950">{feature.label}</p>
                      <p className="text-[11px] text-foreground-500">{feature.desc}</p>
                    </div>
                    <button
                      onClick={() => handleToggle(feature.key)}
                      className={`relative w-11 h-6 rounded-full transition-colors cursor-pointer ${features[feature.key] ? 'bg-accent-500' : 'bg-background-200/50'}`}
                    >
                      <span className={`absolute top-0.5 w-5 h-5 rounded-full bg-white transition-transform ${features[feature.key] ? 'translate-x-[22px]' : 'translate-x-0.5'}`} />
                    </button>
                  </div>
                ))}
                <div className="flex justify-end pt-4">
                  <button onClick={() => handleSave('features')} disabled={saving} className="px-6 py-2.5 rounded-xl bg-accent-500 text-white text-sm font-semibold hover:bg-accent-400 disabled:opacity-60 cursor-pointer whitespace-nowrap">
                    {saving ? 'Saving...' : 'Save Toggles'}
                  </button>
                </div>
              </div>
            )}

            {/* ── Social & Branding ── */}
            {activeTab === 'social' && (
              <div className="rounded-xl border border-background-200/60 bg-white p-6 space-y-4">
                <h3 className="text-sm font-semibold text-foreground-950 mb-4">Social Media Links</h3>
                {[
                  { label: 'Facebook', icon: 'ri-facebook-fill', key: 'facebook' as const, placeholder: 'https://facebook.com/cashmyproperty' },
                  { label: 'Instagram', icon: 'ri-instagram-line', key: 'instagram' as const, placeholder: 'https://instagram.com/cashmyproperty' },
                  { label: 'Twitter / X', icon: 'ri-twitter-x-line', key: 'twitter' as const, placeholder: 'https://x.com/cashmyproperty' },
                  { label: 'LinkedIn', icon: 'ri-linkedin-fill', key: 'linkedin' as const, placeholder: 'https://linkedin.com/company/cashmyproperty' },
                  { label: 'YouTube', icon: 'ri-youtube-fill', key: 'youtube' as const, placeholder: 'https://youtube.com/@cashmyproperty' },
                ].map(link => (
                  <div key={link.label} className="flex items-center gap-3">
                    <span className="w-10 h-10 rounded-lg bg-background-200/60 flex items-center justify-center flex-shrink-0">
                      <i className={`${link.icon} text-foreground-500 text-lg`} />
                    </span>
                    <div className="flex-1">
                      <label className="text-[10px] font-semibold text-foreground-500 uppercase tracking-wider block mb-1">{link.label}</label>
                      <input
                        type="url"
                        value={social[link.key]}
                        onChange={e => setSocial(prev => ({ ...prev, [link.key]: e.target.value }))}
                        placeholder={link.placeholder}
                        className="w-full px-4 py-2.5 rounded-xl bg-white border border-background-200/60 text-sm text-foreground-950 placeholder:text-foreground-500 focus:outline-none focus:border-accent-500/50"
                      />
                    </div>
                  </div>
                ))}
                <div className="flex justify-end pt-2">
                  <button onClick={() => handleSave('social')} disabled={saving} className="px-6 py-2.5 rounded-xl bg-accent-500 text-white text-sm font-semibold hover:bg-accent-400 disabled:opacity-60 cursor-pointer whitespace-nowrap">
                    {saving ? 'Saving...' : 'Save Links'}
                  </button>
                </div>
              </div>
            )}

            {/* ── SEO Meta Tags ── */}
            {activeTab === 'seo' && (
              <div className="space-y-5">
                {/* Default SEO */}
                <div className="rounded-xl border border-background-200/60 bg-white p-6 space-y-4">
                  <div className="flex items-center justify-between">
                    <div>
                      <h3 className="text-sm font-semibold text-foreground-950">Default Site-Wide SEO</h3>
                      <p className="text-[11px] text-foreground-500">Fallback values for pages without custom overrides</p>
                    </div>
                  </div>

                  <div>
                    <label className="text-[10px] font-semibold text-foreground-500 uppercase tracking-wider block mb-1.5">Default Page Title</label>
                    <input type="text" value={seoDefaults.default_title} onChange={e => setSeoDefaults(prev => ({ ...prev, default_title: e.target.value }))} className="w-full px-4 py-2.5 rounded-xl bg-white border border-background-200/60 text-sm text-foreground-950 placeholder:text-foreground-500 focus:outline-none focus:border-accent-500/50" />
                    <p className="text-[10px] text-foreground-400 mt-1">{seoDefaults.default_title.length}/60 chars (recommended max)</p>
                  </div>

                  <div>
                    <label className="text-[10px] font-semibold text-foreground-500 uppercase tracking-wider block mb-1.5">Default Meta Description</label>
                    <textarea rows={3} value={seoDefaults.default_description} onChange={e => setSeoDefaults(prev => ({ ...prev, default_description: e.target.value }))} className="w-full px-4 py-2.5 rounded-xl bg-white border border-background-200/60 text-sm text-foreground-950 placeholder:text-foreground-500 focus:outline-none focus:border-accent-500/50 resize-none" />
                    <p className="text-[10px] text-foreground-400 mt-1">{seoDefaults.default_description.length}/160 chars (recommended max)</p>
                  </div>

                  <div>
                    <label className="text-[10px] font-semibold text-foreground-500 uppercase tracking-wider block mb-1.5">Default Keywords</label>
                    <input type="text" value={seoDefaults.default_keywords} onChange={e => setSeoDefaults(prev => ({ ...prev, default_keywords: e.target.value }))} className="w-full px-4 py-2.5 rounded-xl bg-white border border-background-200/60 text-sm text-foreground-950 placeholder:text-foreground-500 focus:outline-none focus:border-accent-500/50" placeholder="keyword1, keyword2, keyword3" />
                  </div>

                  <div>
                    <label className="text-[10px] font-semibold text-foreground-500 uppercase tracking-wider block mb-1.5">OG / Social Share Image URL</label>
                    <input type="url" value={seoDefaults.og_image_url} onChange={e => setSeoDefaults(prev => ({ ...prev, og_image_url: e.target.value }))} className="w-full px-4 py-2.5 rounded-xl bg-white border border-background-200/60 text-sm text-foreground-950 placeholder:text-foreground-500 focus:outline-none focus:border-accent-500/50" placeholder="https://example.com/og-image.jpg" />
                  </div>
                </div>

                {/* Per-Page Overrides */}
                <div className="rounded-xl border border-background-200/60 bg-white p-6 space-y-4">
                  <div className="flex items-center justify-between">
                    <div>
                      <h3 className="text-sm font-semibold text-foreground-950">Per-Page SEO Overrides</h3>
                      <p className="text-[11px] text-foreground-500">Custom titles and descriptions for individual pages ({overrideFilledCount}/{seoPages.length} configured)</p>
                    </div>
                  </div>

                  <div className="space-y-3">
                    {seoPages.map(page => {
                      const title = getOverride(page, 'title');
                      const desc = getOverride(page, 'description');
                      const kw = getOverride(page, 'keywords');
                      const isConfigured = title || desc || kw;
                      return (
                        <details key={page} className="group border border-background-200/60 rounded-xl overflow-hidden">
                          <summary className="flex items-center justify-between px-4 py-3 cursor-pointer hover:bg-background-50 transition-colors">
                            <div className="flex items-center gap-3">
                              <i className={`ri-arrow-right-s-line text-foreground-400 text-sm transition-transform group-open:rotate-90`} />
                              <span className="text-sm font-medium text-foreground-950 capitalize">{page.replace(/-/g, ' ')}</span>
                              {isConfigured && <span className="w-1.5 h-1.5 rounded-full bg-accent-500" />}
                            </div>
                            <span className="text-[10px] text-foreground-400">{isConfigured ? 'Customized' : 'Using defaults'}</span>
                          </summary>
                          <div className="px-4 pb-4 pt-1 space-y-3 border-t border-background-200/60">
                            <div>
                              <label className="text-[9px] font-semibold text-foreground-500 uppercase tracking-wider block mb-1">Title <span className="text-foreground-400 font-normal">(overrides default)</span></label>
                              <input type="text" value={title} onChange={e => handleOverrideChange(page, 'title', e.target.value)} className="w-full px-3 py-2 rounded-lg bg-white border border-background-200/60 text-sm text-foreground-950 placeholder:text-foreground-500 focus:outline-none focus:border-accent-500/50" placeholder={`Leave empty to use default`} />
                              <p className="text-[9px] text-foreground-400 mt-0.5">{title.length}/60 chars</p>
                            </div>
                            <div>
                              <label className="text-[9px] font-semibold text-foreground-500 uppercase tracking-wider block mb-1">Meta Description</label>
                              <textarea rows={2} value={desc} onChange={e => handleOverrideChange(page, 'description', e.target.value)} className="w-full px-3 py-2 rounded-lg bg-white border border-background-200/60 text-sm text-foreground-950 placeholder:text-foreground-500 focus:outline-none focus:border-accent-500/50 resize-none" placeholder="Leave empty to use default" />
                              <p className="text-[9px] text-foreground-400 mt-0.5">{desc.length}/160 chars</p>
                            </div>
                            <div>
                              <label className="text-[9px] font-semibold text-foreground-500 uppercase tracking-wider block mb-1">Keywords</label>
                              <input type="text" value={kw} onChange={e => handleOverrideChange(page, 'keywords', e.target.value)} className="w-full px-3 py-2 rounded-lg bg-white border border-background-200/60 text-sm text-foreground-950 placeholder:text-foreground-500 focus:outline-none focus:border-accent-500/50" placeholder="Leave empty to use default" />
                            </div>
                          </div>
                        </details>
                      );
                    })}
                  </div>
                </div>

                <div className="flex justify-end">
                  <button onClick={() => handleSave('seo')} disabled={saving} className="px-6 py-2.5 rounded-xl bg-accent-500 text-white text-sm font-semibold hover:bg-accent-400 disabled:opacity-60 cursor-pointer whitespace-nowrap">
                    {saving ? 'Saving...' : 'Save SEO Settings'}
                  </button>
                </div>
              </div>
            )}
          </>
        )}
      </div>
    </DashboardLayout>
  );
}